import * as React from 'react';
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {Navigate} from "react-router-dom";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {
    FalconNavigatorHeader,
    FalconPaymentRequest,
    FalconPaymentResponse,
    ICustomerPortalService,
    InstalmentsInfo,
    Party,
    PaymentCard,
    Policy,
    PolicyProperty,
    PolicyPropertyEnum,
    PROCESS_PAY_OFF_BALANCE,
    PRODUCT_ANNUAL_HOME,
    Types
} from "@falcon/services";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {CardPaymentPanel} from "../../../../../components/payments/CardPaymentPanel";
import {lazyInject} from "@athena-portal/core";
import {findCardToReuse, getAmountToDisplay, getProductByPolicyNumber} from "../../../../../CommonFunctions";
import {FailedPaymentMessage} from "../../../../../components/payments/FailedPaymentMessage";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";

interface IPageParams {
    policyNumber: string;
    [key: string]: string | undefined;
}

interface IState {
    payOffAmount?: number;
    party?: Party;
    paymentFailed: boolean;
    showOverlay: boolean;
    reuseCard?: boolean;
    mainCardId?: number;
    propertiesLoaded: boolean;
    inFlightInstalmentPremium?: number;
    remainingInterest?: number;
    policy?: Policy,
    directDebitSupported: boolean;
}

@authorize
export class PayOffBalancePagePlain extends React.Component<IWithRouterProps<IPageParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps<IPageParams>) {
        super(props);

        this.state = {
            paymentFailed: false,
            showOverlay: false,
            propertiesLoaded: false,
            directDebitSupported: false
        }

        this.handleExistingCardPayoff = this.handleExistingCardPayoff.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
    }

    public componentDidMount(): void {
        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output
            })
        });

        this.customerPortalService.getInstalmentsInfoByAgreementNumber(this.props.params.policyNumber).subscribe((output: InstalmentsInfo) => {
            this.setState({
                payOffAmount: output.payOffAmount,
                remainingInterest: output.remainingInterest,
                // Realistically there should only be 1 IN_FLIGHT invoice/instalment
                inFlightInstalmentPremium: output.instalments.filter(instalment => instalment.status === "IN_FLIGHT").map(instalment => instalment.amount).reduce((a, b) => a + b, 0),
            })
        });

        this.customerPortalService.getPolicyPropertiesByPropertyKeys(this.props.params.policyNumber,
            [PolicyPropertyEnum.REUSE_MAIN_CARD, PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD, PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED]).subscribe(
            (properties: PolicyProperty[]) => {
                properties.forEach(property => {
                    if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                        this.setState({reuseCard: property.value === "true"});
                    } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                        this.setState({mainCardId: +property.value});
                    } else if (property.key === PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED) {
                        this.setState({directDebitSupported: property.value === "true"});
                    }
                })
                this.setState({propertiesLoaded: true})
            }
        );

        this.customerPortalService.getPolicy(this.props.params.policyNumber).subscribe(policy => {
                this.setState({policy});
            }
        )
    }

    public render(): React.ReactNode {
        const backLinkUrl: string = `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`;

        if (this.state.party && this.state.payOffAmount && this.state.propertiesLoaded) {
            const mainCard: PaymentCard | undefined = findCardToReuse(this.state.party, this.state.reuseCard, this.state.mainCardId);
            const isHome: boolean = !!this.state.policy?.products.includes(PRODUCT_ANNUAL_HOME);

            if (mainCard) {
                return (
                    <>
                        <FalconHeader/>
                        {this.state.paymentFailed && <FailedPaymentMessage directDebitSupported={this.state.directDebitSupported}/>}
                        <div className="page-contents">
                            <AllianzOnlineBadge/>
                            <FalconNavigatorHeader back={backLinkUrl} title="Pay off my balance"/>
                            {!!this.state.inFlightInstalmentPremium &&
                                <div className="margin-bottom-32">
                                        <div className="warning-banner">
                                        <img src="/img/warning-filled.svg"
                                             alt="Orange warning icon"/>
                                        <div>Please don't cancel your Direct Debit until your next instalment is collected.</div>
                                    </div>
                                </div>}
                            <div className="panel policy-change-price">
                                <div className="price font-family-display font-weight-bold text-center">
                                    {getAmountToDisplay(this.state.payOffAmount)}
                                </div>
                                <p className="text-center margin-bottom-24">to pay in full for your {isHome ? "home" : "car"} insurance.
                                    {!!this.state.inFlightInstalmentPremium ? ` Please note this excludes your next instalment
                                    of ${getAmountToDisplay(this.state.inFlightInstalmentPremium)}.` : ""}</p>
                                <p className="text-center margin-bottom-24">By paying off your {isHome ? "home" : "car"} insurance, you're
                                    saving {getAmountToDisplay(this.state.remainingInterest)} in interest charges. There
                                    are no fees on doing that.</p>
                                <p className="text-center margin-bottom-24">All figures include Insurance Premium
                                    Tax.</p>
                            </div>
                            <CardPaymentPanel amount={this.state.payOffAmount}
                                party={this.state.party}
                                handleExistingCardPayment={this.handleExistingCardPayoff}
                                newCardUrl={`/dashboard/${this.props.params.policyNumber}/payOffBalance/newCard`}
                                mainCard={mainCard}
                            />
                            <button type="button"
                                    className="btn btn-default btn-white-dashboard m-auto"
                                    onClick={this.cancelChanges}>
                                Cancel changes
                            </button>
                            <div>
                                {this.pluginTemplates.renderBackLink("Back", false, backLinkUrl)}
                            </div>
                        </div>
                        <FalconFooter/>
                        <Overlay show={this.state.showOverlay}/>
                    </>
                )
            } else {
                return (
                    <Navigate to={`/dashboard/${this.props.params.policyNumber}/payOffBalance/newCard`}
                            state={{
                                paymentFailed: false,
                                payOffAmount: this.state.payOffAmount
                            }}/>
                )
            }
        } else {
            return <Overlay show={true}/>
        }
    }

    private handleExistingCardPayoff(event: any, cardId: number | undefined): void {
        event.preventDefault();

        this.setState({
            showOverlay: true
        })

        const request: FalconPaymentRequest = new FalconPaymentRequest();
        request.invoiceType = "PAYOFF";
        request.cardId = cardId;
        request.product = getProductByPolicyNumber(this.props.params.policyNumber);

        this.customerPortalService.payOffBalance(request, this.props.params.policyNumber).subscribe((response: FalconPaymentResponse) => {
            if (response.success) {
                window.sessionStorage.setItem("showPopup", "true");
                window.sessionStorage.setItem("referrerJourney", PROCESS_PAY_OFF_BALANCE);
                window.sessionStorage.setItem("paymentAmount", getAmountToDisplay(this.state.payOffAmount));

                window.location.href = `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`;
            } else {
                this.setState({
                    paymentFailed: true
                })
            }

            this.setState({
                showOverlay: false
            })
        })
    }

    private cancelChanges(): void {
        this.props.navigate(`/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`);
    }

}

export const PayOffBalancePage = withRouter<{}, IPageParams>(PayOffBalancePagePlain)
